import { applyChanges } from "../fsm/applyChanges";
import { Change } from "../fsm/change";
import { PhonemeInfo } from "../pattern/phonemeInfo";
import { IpaType } from "../phonemes/ipa";
import { PhonemeItem, segmentPhonemes } from "../phonemes/segmentPhonemes";
import { SyllablePhonotactics } from "../phonotactics/phonotactics";
import { applyStressRule } from "../stress/applyStressRule";
import { StressRule } from "../stress/stressRule";
import { handleNumbers } from "../transcribe/numberToEnglish";
import { nonIpaPunctuation, plainPunctuation, wordsToIpa } from "../transcribe/wordsToIpa";
import { applyRules } from "./applyRules";
import { CompiledRule } from "./compileRule";
import { DiacriticRule } from "./diacriticRule";
import { findSetsForIpa, findSetsForWord } from "./findLexicalSets";
import { intoSegments, SegmentStrategy } from "./intoSegments";
import { replaceLexicalTokens } from "./replaceLexicalSets";
import { CompiledRuleSet, PreprocessOptions } from "./ruleSet";
import { addSyllableBreaks } from "./addSyllableBreaks";
import { parseUnits } from "../units/parseUnits";
import { WordUnit, isAsIs, isPunctuation } from "../units/wordUnit";
import { unitToOutput } from "../units/applyToIpa";
import { en_US_Abbrevs } from "../transcribe/en_US-abbrev";
import { cleanLigatures } from "../units/cleanLigatures";
import { normIpa } from "./normIpa";

/** raw sentence (e.g. English) -> rules -> IPA, + any words not in dictionary */
export function runRulesOnRaw(raw: string, ruleSet: CompiledRuleSet, fallback?: (word: string) => string): [IpaType, string[]] {
  const preOptions = ruleSet.preprocess ?? [];
  const raw2 = preOptions.includes('cleanLigatures') ? preprocess(raw, ['cleanLigatures']) : raw;
  const unitOptions = { pmd: true, punctuation: preOptions.includes('cleanPunctuation'), lowerCase: preOptions.includes('lowerCase') };
  const units = parseUnits(raw2, en_US_Abbrevs, unitOptions);

  const afterNumbers = units.map(unit => isPunctuation(unit) ? unit : handleNumbers(unit));
  const afterWholeWords = afterNumbers.map(unit => replaceWord(unit, ruleSet.wholeWords));

  const afterLexicalSets = afterWholeWords.map(unit => applySetsToRaw(unit, ruleSet.lexicalSetLookup, ruleSet.useRPDictionary, fallback));
  const notFound = afterLexicalSets.flatMap(s => s[1])
  const justWords = afterLexicalSets.map(s => s[0]);
  const withBreaks = ruleSet.syllableBreaks ? addAllSyllableBreaks(justWords, ruleSet.syllableBreaks, ruleSet.inputSegments) : justWords;

  const afterRules = withBreaks.map(word => applyTheRules(word, ruleSet.inputSegments, ruleSet.subsetMatches,
    ruleSet.rules, ruleSet.diacriticRules, ruleSet.rules2));
  const noEmpty = afterRules.filter(unit => unit.output && unit.output !== '');

  const afterFsm = ruleSet.fsm ? fsmOnWords(noEmpty, ruleSet.fsm) : noEmpty;

  const stressRule = ruleSet.stressRule ? afterFsm.map(unit =>
    runStressRule(unit, ruleSet.stressRule, ruleSet.phonotactics, ruleSet.outputSegments))
    : afterFsm;

  const final = stressRule.map(u => unitToOutput(u));
  const ipaString = final.join(' ');
  return [ipaString, notFound];
}

export interface ApplyRulesResult {
  ipa: IpaType;
  hasUnknowns?: PhonemeItem[];
}

/** IPA -> rules (but not wholeWords) -> IPA */
export function runRulesOnIpa(ipa: IpaType, ruleSet: CompiledRuleSet): ApplyRulesResult {
  ipa = preprocess(ipa, ruleSet.preprocess);
  const hasUnknowns = ruleSet.inputSegments === SegmentStrategy.Single ? false : validateIpa(ipa, ruleSet.inputSegments);

  const ipaWords = ipaToWords(ipa);
  const afterLexicalSets = ipaWords.map(word => applySetsToIpa(word, ruleSet.lexicalSetLookup));
  const withBreaks = ruleSet.syllableBreaks ? addAllSyllableBreaks(afterLexicalSets, ruleSet.syllableBreaks, ruleSet.inputSegments) : afterLexicalSets;

  const ignoreNumbers = (ruleSet.inputSegments === SegmentStrategy.IpaPlus || ruleSet.inputSegments === SegmentStrategy.SynthSymbols ? '#' : undefined);
  const afterRules = withBreaks.map(word => applyTheRules(word, ruleSet.inputSegments, ruleSet.subsetMatches,
    ruleSet.rules, ruleSet.diacriticRules, ruleSet.rules2, ignoreNumbers));
  const allIpa = afterRules.map(word => word.output);
  const justIpa = allIpa.filter(ipa => ipa !== undefined && ipa !== '').map(ipa => { return { input: ipa, output: ipa } }) as WordUnit[];

  const afterFsm = ruleSet.fsm ? fsmOnWords(justIpa, ruleSet.fsm) : justIpa;

  const stressRule = ruleSet.stressRule ? afterFsm.map(unit =>
    runStressRule(unit, ruleSet.stressRule, ruleSet.phonotactics, ruleSet.outputSegments))
    : afterFsm;
  const result = stressRule.map(u => u.output).join(' ');
  return hasUnknowns ? { ipa: result, hasUnknowns } : { ipa: result };
}

function preprocess(s: string, preprocess?: PreprocessOptions[]): string {
  if (preprocess && preprocess.includes('lowerCase')) {
    s = s.toLowerCase();
  }
  if (preprocess && preprocess.includes('cleanLigatures')) {
    s = cleanLigatures(s);
  }
  if (preprocess && preprocess.includes('cleanPunctuation')) {
    s = s.replace(/\n|\t/g, ' ‖ ');
    s = s.replace(/’/g, "'"); // so we can look up contractions
    // don't leave punctuation next to phonemes - signal to the speech routines to insert a pause
    s = s.replace(/, /g, ' | ');
    s = s.replace(/:|“|”|‘|’|—|–|\(|\)/g, ' | ');
    s = s.replace(/…|;/g, ' ‖ ');
    s = s.replace(/\. | \.|\.$/g, ' ‖ '); // only if . is next to a space or end of string
    s = s.replace(/\?/g, ' ? ');
    s = s.replace(/!/g, ' ! ');
    s = s.replace(/¿|¡/g, ' ');

    const len = s.length;
    if (s[len - 1] === ',' && s[len - 2] !== ' ') {
      s = s.substring(0, len - 1) + ' | ';
    }
  }
  if (preprocess && preprocess.includes('normIpa')) {
    s = normIpa(s);
  }
  return s;
}

export function validateIpa(ipa: IpaType, theType: SegmentStrategy): PhonemeItem[] | undefined {
  const phonemes = segmentPhonemes(ipa, theType === SegmentStrategy.IpaPlus ? 'ipaPlus' : 'ipa');
  if (!phonemes.hasUnknown) {
    return undefined;
  }
  return phonemes.phonemes.filter(p => p.meta === 'unknown');
}

function ipaToWords(raw: string): WordUnit[] {
  const words = raw.split(/\s/g);
  const withoutEmpty = words.filter(word => word !== '' && word !== ' ');
  return withoutEmpty.map(output => { return { input: '', output } });
}

function replaceWord(unit: WordUnit, wholeWords?: Record<string, IpaType>): WordUnit {
  if (!wholeWords) {
    return unit;
  }
  const output = wholeWords[unit.input];
  return output ? { ...unit, output } : unit;
}

/** returns [word (possibly w/ IPA added), notFound] */
function applySetsToRaw(unit: WordUnit, lexicalSetLookup: string[], useRPDictionary?: boolean, fallback?: (word: string) => string): [WordUnit, string[]] {
  if (unit.output) {
    // if we already have IPA, don't run it thru lexical sets
    return [unit, []];
  } else if (isAsIs(unit)) {
    return [{ ...unit, output: unit.input }, []];
  }
  if (lexicalSetLookup.length === 0) {
    // if no rules, just do a normal lookup
    const dict = useRPDictionary ? 'en_UK' : undefined;
    const [output, notFound] = wordsToIpa(unit.input, dict, fallback);
    return [{ ...unit, output }, notFound];
  }

  const ipaWithSets = findSetsForWord(unit, useRPDictionary, fallback);
  const output = replaceLexicalTokens(ipaWithSets, lexicalSetLookup);
  return [{ ...unit, output }, []];
}

function applySetsToIpa(word: WordUnit, lexicalSetLookup: string[]): WordUnit {
  if (lexicalSetLookup.length === 0 || !word.output) {
    return word;
  } else if (word.output && plainPunctuation.includes(word.output)) {
    return word;
  }

  const ipaWithSets = findSetsForIpa(word.output, '');
  const output = replaceLexicalTokens(ipaWithSets, lexicalSetLookup);
  return { ...word, output };
}

function addAllSyllableBreaks(
  words: WordUnit[], syllableBreaks: SyllablePhonotactics<PhonemeInfo> | IpaType[], strategy: SegmentStrategy
): WordUnit[] {
  const withBreaks = words.map(word => {
    if (!word.output || plainPunctuation.includes(word.output) || isAsIs(word)) {
      return word;
    }
    const output = addSyllableBreaks(word.output, syllableBreaks, strategy);
    return { ...word, output };
  });
  return withBreaks;
}

function applyTheRules(
  word: WordUnit, strategy: SegmentStrategy, subsetMatches?: boolean,
  rules?: CompiledRule[], diacriticRules?: DiacriticRule[], rules2?: CompiledRule[][], ignoreNumbers?: '#'
): WordUnit {
  if (!rules || isAsIs(word)) {
    return word;
  } else if (word.output && nonIpaPunctuation.includes(word.output)) {
    return word;
  }

  const [hasUnknowns, phonemes] = intoSegments(word.output ? word.output.trim() : '', strategy);
  if (hasUnknowns) {
    return { ...word, output: undefined };
  }
  const output = applyRules(phonemes, rules, subsetMatches, diacriticRules, rules2, ignoreNumbers);
  return { ...word, output };
}

function fsmOnWords(words: WordUnit[], changes: Change[]): WordUnit[] {
  return words.map(unit => {
    if (!unit.output) {
      return unit;
    }
    const { output } = applyChanges(unit.output, changes);
    return { ...unit, output };
  });
}

function runStressRule(
  unit: WordUnit,
  stressRule: StressRule<PhonemeInfo> | undefined,
  phonotactics: SyllablePhonotactics<PhonemeInfo> | IpaType[],
  strategy: SegmentStrategy
): WordUnit {
  if (!unit.output || stressRule === undefined) {
    return unit;
  }
  // by instead segmenting with modifiers (e.g. tʰ),
  // we avoid putting stress before the modifier or trailing diacritic (e.g. tʰː)
  const s = strategy === SegmentStrategy.Ipa ? SegmentStrategy.IpaModifier : strategy;
  const [hasUnknowns, phonemes] = intoSegments(unit.output, s);
  if (hasUnknowns) {
    return unit;
  }
  const pieces = phonemes.map(p => p.phoneme);
  const result = applyStressRule(pieces, stressRule, phonotactics);
  if (result === false) {
    return unit;
  }
  const output = result.join('');
  return { ...unit, output };
}
